import { memo, useCallback } from "react";
import type {
  ActiveDataTable,
  ActiveInspectionRegister,
  ClosedDataTable,
  ClosedInspectionRegister,
  InspectionState,
} from "../InspectionsLists.d";
import { useTranslate } from "@/hooks/use-translate/useTranslate";
import InspectionsMobileSearcher from "../inspection-mobile-searcher/InspectionsMobileSearcher";
import PrimaryButton from "@/components/buttons/primary-button/PrimaryButton";
import ActiveInspectionCard from "../inspections-mobile-cards/ActiveInspectionCard";
import Pagination from "../Pagination/Pagination";
import ClosedInspectionCard from "../inspections-mobile-cards/ClosedInspectionCard";
import SecondaryButton from "@/components/buttons/secondary-button/SecondaryButton";
import { getIsFiltered } from "../utils/inspections.utils";
import { cn } from "@/lib/styles.utils";
import { useUserStore } from "@/store/useUser";

export interface InspectionsMobileListProps {
  activeList: InspectionState;
  listData: ActiveDataTable | ClosedDataTable;
  changePage: (
    page: number,
    status: InspectionState | undefined,
    notFromFiltered?: boolean | undefined,
  ) => void;
  searchInspections: (params: string) => void;
  getNoSortedInspections: () => void;
  cleanSearch: () => void;
  addInspection: () => void;
  openedCreator: boolean;
  lastFiltered: string;
  newPagination?: boolean;
}

const InspectionsMobileList = ({
  activeList,
  listData,
  changePage,
  searchInspections,
  getNoSortedInspections,
  cleanSearch,
  addInspection,
  openedCreator,
  newPagination,
  lastFiltered,
}: InspectionsMobileListProps) => {
  const translate = useTranslate();

  const { isSupervisor } = useUserStore();

  const renderInspectionsList = useCallback(() => {
    if (activeList === "active")
      return listData.data.map((inspection) => (
        <ActiveInspectionCard
          key={`active-${inspection.id}`}
          inspectionData={inspection as ActiveInspectionRegister}
          getNoSortedInspections={getNoSortedInspections}
        />
      ));
    return listData.data.map((inspection) => (
      <ClosedInspectionCard
        key={`closed-${inspection.id}`}
        inspectionData={inspection as ClosedInspectionRegister}
      />
    ));
  }, [activeList, listData.data, getNoSortedInspections]);

  const getNoFilteredInspections = useCallback(() => {
    cleanSearch();
    getNoSortedInspections();
  }, [cleanSearch, getNoSortedInspections]);

  return (
    <div className="grid grid-rows-[1fr_auto] h-full min-h-0 max-w-full">
      <div
        id="scrollable"
        className="min-h-0 overflow-y-auto overflow-x-auto max-w-full bg-ib-gray-lightest-2"
      >
        <div
          className={cn(
            "px-4 pb-2 overflow-y-auto bg-ib-gray-lightest-2 h-full",
          )}
        >
          <InspectionsMobileSearcher
            disabled={listData.data.length < 1}
            tableType={activeList}
            searchInspections={searchInspections}
            cleanSearch={cleanSearch}
            openedCreator={openedCreator}
            lastFiltered={lastFiltered}
            getNoSortedInspections={getNoSortedInspections}
          />
          <div className="w-full flex flex-col gap-y-2">
            {listData.data.length > 0 && getIsFiltered(lastFiltered) ? (
              <div className="w-full flex flex-row justify-center">
                <div className="w-fit">
                  <SecondaryButton
                    colour="red"
                    size="medium"
                    icon="closeEmptyCircle"
                    onClick={getNoFilteredInspections}
                    label={translate("cleanFilters")}
                  />
                </div>
              </div>
            ) : null}
            {!listData.data.length ? (
              <div className="mt-2 w-full flex flex-col items-center  p-6 border border-ib-gray-lightest bg-ib-white text-center text-[16px] leading-[18px] text-ib-gray-main">
                <div>
                  {lastFiltered?.length > 0
                    ? translate("noSearchResults")
                    : translate(
                        activeList === "active"
                          ? "noOpenInspections"
                          : "noClosedInspections",
                      )}
                </div>
                {lastFiltered?.length > 0 ? (
                  <div className="mt-4 w-fit">
                    <SecondaryButton
                      colour="red"
                      size="medium"
                      icon="closeEmptyCircle"
                      onClick={getNoFilteredInspections}
                      label={translate("cleanFilters")}
                    />
                  </div>
                ) : null}
              </div>
            ) : (
              renderInspectionsList()
            )}
            {listData.pages && listData.pages > 1 ? (
              <div className="mt-1">
                <Pagination
                  lastPage={listData.pages}
                  changePage={changePage}
                  newPagination={newPagination}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>
      {!isSupervisor ? (
        <div className="w-full border-t border-t-ib-gray-borders p-4 bg-ib-white">
          <PrimaryButton
            label={translate("newInspection")}
            size="medium"
            colour="red"
            icon="circlePlus"
            onClick={addInspection}
          />
        </div>
      ) : null}
    </div>
  );
};

export default memo(InspectionsMobileList);
